import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Typography, Table, Tag, Row, Col, DatePicker, Button, Tabs, Spin,
  Empty, message, Modal, Form, Select, InputNumber, Input, Popconfirm, Segmented,
} from 'antd';
import {
  DollarOutlined, RiseOutlined, FallOutlined, PlusOutlined, FileDoneOutlined,
  PrinterOutlined, WalletOutlined, BarChartOutlined, DeleteOutlined,
} from '@ant-design/icons';
import api from '../../../api/axios';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const fmt = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

function Moliya() {
  const printRef = useRef();
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [tab, setTab] = useState('dashboard');

  const [summary, setSummary] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(false);

  const [cfFilter, setCfFilter] = useState('all');
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryForm] = Form.useForm();

  const params = useCallback(() => {
    const from = range[0].format('YYYY-MM-DD');
    const to = range[1].format('YYYY-MM-DD');
    return `from=${from}&to=${to}`;
  }, [range]);

  const fetchTab = useCallback(() => {
    setLoading(true);
    const p = params();
    const url = tab === 'dashboard' ? `/finance/summary?${p}`
      : tab === 'cashflow' ? `/finance/cashflow?${p}`
        : `/finance/pnl?${p}`;
    api.get(url)
      .then(res => {
        const d = res.data?.data;
        if (tab === 'dashboard') setSummary(d);
        else if (tab === 'cashflow') setCashflow(d);
        else setPnl(d);
      })
      .catch(() => message.error('Moliyaviy ma\'lumotlarni yuklashda xatolik'))
      .finally(() => setLoading(false));
  }, [tab, params]);

  useEffect(() => { fetchTab(); }, [fetchTab]);

  const refreshAll = () => {
    // joriy tabni qayta yuklash
    fetchTab();
  };

  const addEntry = () => {
    entryForm.validateFields().then(v => {
      api.post('/finance/entries', {
        type: v.type, category: v.category || null, amount: v.amount,
        date: v.date ? v.date.format('YYYY-MM-DD') : undefined, note: v.note || null,
      }).then(() => {
        message.success('Yozuv qo\'shildi');
        setEntryOpen(false); entryForm.resetFields();
        refreshAll();
      }).catch(err => message.error(err.response?.data?.message || 'Xatolik'));
    });
  };

  const deleteEntry = (id) => {
    api.delete(`/finance/entries/${id}`)
      .then(() => { message.success('O\'chirildi'); fetchTab(); })
      .catch(() => message.error('Xatolik'));
  };

  // ─── DASHBOARD ───
  const renderDashboard = () => {
    if (!summary) return null;
    const trendMax = Math.max(1, ...summary.trend.flatMap(t => [t.income, t.expense]));
    const eb = summary.expense_breakdown;
    const ib = summary.income_breakdown;
    const expItems = [
      { l: 'Buyurtma xarajatlari', v: eb.orders, c: '#ef4444' },
      { l: 'Maoshlar', v: eb.salaries, c: '#f59e0b' },
      { l: 'Platforma tarifi', v: eb.platform, c: '#8b5cf6' },
      { l: 'Qo\'lda chiqimlar', v: eb.manual, c: '#64748b' },
    ].filter(x => x.v > 0);

    return (
      <>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Card style={{ borderRadius: 12, background: 'rgba(34,197,94,0.12)' }}>
              <Text type="secondary">Daromad (kirim)</Text>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}><RiseOutlined /> {fmt(summary.income)} so'm</div>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ borderRadius: 12, background: 'rgba(239,68,68,0.12)' }}>
              <Text type="secondary">Xarajat (chiqim)</Text>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}><FallOutlined /> {fmt(summary.expense)} so'm</div>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ borderRadius: 12, background: summary.profit >= 0 ? 'rgba(102,126,234,0.12)' : 'rgba(245,158,11,0.12)' }}>
              <Text type="secondary">Sof foyda</Text>
              <div style={{ fontSize: 24, fontWeight: 700, color: summary.profit >= 0 ? '#667eea' : '#f59e0b' }}>
                <DollarOutlined /> {fmt(summary.profit)} so'm
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24} lg={14}>
            <Card style={{ borderRadius: 12 }} title={<span><BarChartOutlined /> 6 oylik dinamika</span>}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 200, padding: '0 8px' }}>
                {summary.trend.map((t, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 3, width: '100%', justifyContent: 'center' }}>
                      <div title={`Kirim: ${fmt(t.income)}`} style={{ width: '40%', background: '#22c55e', borderRadius: '4px 4px 0 0', height: `${(t.income / trendMax) * 100}%`, minHeight: t.income ? 3 : 0 }} />
                      <div title={`Chiqim: ${fmt(t.expense)}`} style={{ width: '40%', background: '#ef4444', borderRadius: '4px 4px 0 0', height: `${(t.expense / trendMax) * 100}%`, minHeight: t.expense ? 3 : 0 }} />
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>{t.label}</Text>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 10 }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#22c55e', borderRadius: 2, marginRight: 4 }} />Kirim</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#ef4444', borderRadius: 2, marginRight: 4 }} />Chiqim</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card style={{ borderRadius: 12 }} title="Xarajatlar tarkibi">
              {expItems.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Xarajat yo'q" /> : expItems.map((x, i) => {
                const pct = summary.expense ? Math.round((x.v / summary.expense) * 100) : 0;
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                      <span>{x.l}</span><b>{fmt(x.v)} so'm ({pct}%)</b>
                    </div>
                    <div style={{ height: 8, background: 'var(--qk-bg-soft)', borderRadius: 4 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: x.c, borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--qk-border)', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Buyurtma daromadi</span><b style={{ color: '#22c55e' }}>{fmt(ib.orders)} so'm</b></div>
                {ib.manual > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Qo'lda kirim</span><b style={{ color: '#22c55e' }}>{fmt(ib.manual)} so'm</b></div>}
              </div>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  // ─── CASHFLOW ───
  const cfRows = (cashflow?.transactions || []).filter(t => cfFilter === 'all' || t.type === cfFilter);
  const cfColumns = [
    { title: 'Sana', dataIndex: 'date', width: 110, render: v => String(v).slice(0, 10) },
    { title: 'Tur', dataIndex: 'type', width: 90, render: v => v === 'income'
      ? <Tag color="green">Kirim</Tag> : <Tag color="red">Chiqim</Tag> },
    { title: 'Kategoriya', dataIndex: 'category', width: 150 },
    { title: 'Tavsif', dataIndex: 'description', ellipsis: true },
    { title: 'Summa', dataIndex: 'amount', align: 'right', width: 150,
      render: (v, r) => <b style={{ color: r.type === 'income' ? '#22c55e' : '#ef4444' }}>
        {r.type === 'income' ? '+' : '−'}{fmt(v)} so'm</b> },
    { title: '', width: 50, align: 'center', render: (_, r) => r.deletable
      ? <Popconfirm title="O'chirish?" onConfirm={() => deleteEntry(r.id)} okText="Ha" cancelText="Yo'q">
          <Button type="text" danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm> : null },
  ];
  const cfIncome = cfRows.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const cfExpense = cfRows.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

  const renderCashflow = () => (
    <Card style={{ borderRadius: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Segmented value={cfFilter} onChange={setCfFilter} options={[
          { label: 'Barchasi', value: 'all' }, { label: 'Kirim', value: 'income' }, { label: 'Chiqim', value: 'expense' },
        ]} />
        <div style={{ fontSize: 13 }}>
          <span style={{ color: '#22c55e', marginRight: 12 }}>Kirim: <b>{fmt(cfIncome)}</b></span>
          <span style={{ color: '#ef4444' }}>Chiqim: <b>{fmt(cfExpense)}</b></span>
        </div>
      </div>
      <Table size="small" rowKey="key" columns={cfColumns} dataSource={cfRows}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        locale={{ emptyText: <Empty description="Tranzaksiya yo'q" /> }} scroll={{ x: 700 }} />
    </Card>
  );

  // ─── P&L ───
  const renderPnl = () => {
    if (!pnl) return null;
    return (
      <Card style={{ borderRadius: 12 }}
        title={<span><FileDoneOutlined /> Foyda-zarar hisoboti</span>}
        extra={<Button icon={<PrinterOutlined />} onClick={handlePrintPnl}>Chop etish / PDF</Button>}>
        <div ref={printRef}>
          <div className="pnl-print-head" style={{ display: 'none' }}>
            <h2 style={{ textAlign: 'center' }}>FOYDA-ZARAR HISOBOTI</h2>
            <p style={{ textAlign: 'center' }}>{pnl.business || ''} — {pnl.from} … {pnl.to}</p>
          </div>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#22c55e' }}>Daromadlar</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {pnl.income.map((r, i) => (
                    <tr key={i}><td style={tdL}>{r.category}</td><td style={tdR}>{fmt(r.amount)}</td></tr>
                  ))}
                  <tr><td style={{ ...tdL, fontWeight: 700 }}>Jami daromad</td><td style={{ ...tdR, fontWeight: 700, color: '#22c55e' }}>{fmt(pnl.total_income)}</td></tr>
                </tbody>
              </table>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#ef4444' }}>Xarajatlar</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {pnl.expense.map((r, i) => (
                    <tr key={i}><td style={tdL}>{r.category}</td><td style={tdR}>{fmt(r.amount)}</td></tr>
                  ))}
                  <tr><td style={{ ...tdL, fontWeight: 700 }}>Jami xarajat</td><td style={{ ...tdR, fontWeight: 700, color: '#ef4444' }}>{fmt(pnl.total_expense)}</td></tr>
                </tbody>
              </table>
            </Col>
          </Row>
          <div style={{ marginTop: 18, padding: '14px 18px', borderRadius: 10, background: pnl.net_profit >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={5} style={{ margin: 0 }}>SOF FOYDA</Title>
            <Title level={3} style={{ margin: 0, color: pnl.net_profit >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(pnl.net_profit)} so'm</Title>
          </div>
        </div>
      </Card>
    );
  };

  const handlePrintPnl = () => {
    const win = window.open('', '_blank', 'width=800,height=700');
    const head = printRef.current.querySelector('.pnl-print-head').innerHTML;
    win.document.write(`<html><head><title>Foyda-zarar — ${pnl.from}..${pnl.to}</title>
      <style>body{font-family:"Times New Roman",serif;padding:30px;}table{width:100%;border-collapse:collapse;margin-bottom:16px;}td{border:1px solid #ccc;padding:6px 10px;}td:last-child{text-align:right;}h2{margin-bottom:4px;}h3{margin:14px 0 6px;}.net{margin-top:16px;font-size:16px;font-weight:bold;display:flex;justify-content:space-between;border-top:2px solid #000;padding-top:8px;}</style>
      </head><body>${head}
      <h3 style="color:#16a34a">Daromadlar</h3><table><tbody>
      ${pnl.income.map(r => `<tr><td>${r.category}</td><td>${fmt(r.amount)} so'm</td></tr>`).join('')}
      <tr><td><b>Jami daromad</b></td><td><b>${fmt(pnl.total_income)} so'm</b></td></tr></tbody></table>
      <h3 style="color:#dc2626">Xarajatlar</h3><table><tbody>
      ${pnl.expense.map(r => `<tr><td>${r.category}</td><td>${fmt(r.amount)} so'm</td></tr>`).join('')}
      <tr><td><b>Jami xarajat</b></td><td><b>${fmt(pnl.total_expense)} so'm</b></td></tr></tbody></table>
      <div class="net"><span>SOF FOYDA</span><span>${fmt(pnl.net_profit)} so'm</span></div>
      </body></html>`);
    win.document.close(); win.focus(); setTimeout(() => win.print(), 250);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <Title level={4} style={{ margin: 0 }}><WalletOutlined /> Moliya / Buxgalteriya</Title>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <RangePicker value={range} onChange={(v) => v && setRange(v)} allowClear={false} format="DD.MM.YYYY" />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { entryForm.setFieldsValue({ type: 'expense', date: dayjs() }); setEntryOpen(true); }}>
            Yozuv qo'shish
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        <Tabs activeKey={tab} onChange={setTab} items={[
          { key: 'dashboard', label: <span><BarChartOutlined /> Dashboard</span>, children: renderDashboard() },
          { key: 'cashflow', label: <span><DollarOutlined /> Kassa</span>, children: renderCashflow() },
          { key: 'pnl', label: <span><FileDoneOutlined /> Hisobot (P&L)</span>, children: renderPnl() },
        ]} />
      </Spin>

      {/* Qo'lda yozuv modali */}
      <Modal open={entryOpen} onCancel={() => setEntryOpen(false)} onOk={addEntry} okText="Saqlash" title="Qo'lda kirim/chiqim" forceRender destroyOnHidden={false}>
        <Form form={entryForm} layout="vertical" requiredMark={false}>
          <Form.Item name="type" label="Tur" rules={[{ required: true }]}>
            <Select options={[{ label: 'Kirim (daromad)', value: 'income' }, { label: 'Chiqim (xarajat)', value: 'expense' }]} />
          </Form.Item>
          <Form.Item name="category" label="Kategoriya" rules={[{ required: true, message: 'Kategoriya kiriting' }]}>
            <Input placeholder="masalan: Ijara, Kommunal, Reklama, Boshqa daromad" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="amount" label="Summa (so'm)" rules={[{ required: true, message: 'Summa' }, { type: 'number', min: 1 }]}>
                <InputNumber min={1} style={{ width: '100%' }}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="date" label="Sana" rules={[{ required: true, message: 'Sana' }]}>
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label="Izoh"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const tdL = { padding: '6px 10px', borderBottom: '1px solid var(--qk-border)' };
const tdR = { padding: '6px 10px', borderBottom: '1px solid var(--qk-border)', textAlign: 'right' };

export default Moliya;
